import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { useState } from "react";
import SEO from "../SEO";
import { db, smDb } from "../../firebase";
import "../../styles/SecondMarriage.css";

const SUPPORT_EMAIL = "support@macromatrix.io";

const ISSUE_TYPES = [
  { value: "", label: "Select an issue category…" },
  { value: "account_login", label: "🔐 Account / Login issues" },
  { value: "profile", label: "👤 Profile or photo problems" },
  { value: "match_interest", label: "💌 Matches or express interest not working" },
  { value: "chat", label: "💬 Chat / messaging issues" },
  { value: "verification", label: "✅ Verification badge issues" },
  { value: "guardian", label: "🛡️ Guardian (Wali) feature issues" },
  { value: "subscription", label: "⭐ Subscription or payment issues" },
  { value: "notification", label: "🔔 Notification problems" },
  { value: "privacy", label: "🔒 Privacy or account visibility" },
  { value: "report_block", label: "🚨 Report / block not working" },
  { value: "bug", label: "🐛 App crash or bug" },
  { value: "delete_account", label: "🗑️ Delete my account / data" },
  { value: "ai_profile", label: "🤖 AI Profile question" },
  { value: "safety", label: "⚠️ Safety concern or abuse report" },
  { value: "other", label: "📩 Other" },
];

const INITIAL = {
  name: "",
  email: "",
  issueType: "",
  subject: "",
  message: "",
  deviceOs: "",
};

export default function SmContact() {
  const [form, setForm] = useState(INITIAL);
  const [status, setStatus] = useState("idle");

  const set = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.issueType || !form.email || !form.message) return;
    setStatus("sending");

    const issueLabel =
      ISSUE_TYPES.find((o) => o.value === form.issueType)?.label || form.issueType;

    const bodyText = `
Second Marriage – Support Request
===================================
Issue:   ${issueLabel}
Name:    ${form.name}
Email:   ${form.email}
Device:  ${form.deviceOs || "Not specified"}
Subject: ${form.subject || "—"}

Message:
${form.message}

===================================
Submitted: ${new Date().toLocaleString()}
    `.trim();

    try {
      // 1. Save ticket to Firestore (support_requests collection)
      await addDoc(collection(smDb, "support_requests"), {
        ...form,
        issueLabel,
        createdAt: serverTimestamp(),
        status: "open",
      });

      // 2. Write to macro-matrix-web mail collection → Firebase Trigger Email extension fires the email
      await addDoc(collection(db, "mail"), {
        to: SUPPORT_EMAIL,
        from: "Second Marriage Support <support@macromatrix.io>",
        replyTo: form.email,
        message: {
          subject: `[Second Marriage Support] ${issueLabel}${form.subject ? " – " + form.subject : ""}`,
          text: bodyText,
        },
      });

      setStatus("success");
      setForm(INITIAL);
    } catch (err) {
      console.error(err);
      setStatus("error");
    }
  };

  return (
    <div className="sm-legal" style={{ maxWidth: 720 }}>
      <SEO title="Support – Second Marriage" description="Contact Second Marriage support for help with your account, app issues, or general inquiries." path="/secondmarriage/contact" />
      <h1>Contact Support</h1>
      <p style={{ color: "var(--sm-text-2)", marginBottom: 32 }}>
        Having a problem with the Second Marriage app? Fill out the form below
        and our team will get back to you within 24 hours.
      </p>

      {status === "success" ? (
        <div className="sm-contact" style={{ textAlign: "center", padding: "40px 24px" }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
          <h2 style={{ margin: "0 0 8px" }}>Request received!</h2>
          <p style={{ color: "var(--sm-text-2)" }}>
            We'll reply within 24 hours. Check your spam folder if you don't hear from us.
          </p>
          <button
            className="sm-btn sm-btn-primary"
            style={{ marginTop: 20, border: "none", cursor: "pointer" }}
            onClick={() => setStatus("idle")}
          >
            Submit another request
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="sm-support-form">
          <div className="sm-field">
            <label>What's the issue? <span className="sm-req">*</span></label>
            <select value={form.issueType} onChange={set("issueType")} required>
              {ISSUE_TYPES.map((o) => (
                <option key={o.value} value={o.value} disabled={!o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <div className="sm-row">
            <div className="sm-field">
              <label>Your name <span className="sm-req">*</span></label>
              <input type="text" placeholder="Full name" value={form.name} onChange={set("name")} required />
            </div>
            <div className="sm-field">
              <label>Email address <span className="sm-req">*</span></label>
              <input type="email" placeholder="you@example.com" value={form.email} onChange={set("email")} required />
            </div>
          </div>

          <div className="sm-row">
            <div className="sm-field">
              <label>Subject</label>
              <input type="text" placeholder="Brief summary" value={form.subject} onChange={set("subject")} />
            </div>
            <div className="sm-field">
              <label>Device / OS</label>
              <select value={form.deviceOs} onChange={set("deviceOs")}>
                <option value="">Select…</option>
                <option value="ios">iPhone (iOS)</option>
                <option value="android">Android</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>

          <div className="sm-field">
            <label>Describe the issue <span className="sm-req">*</span></label>
            <textarea
              rows={6}
              placeholder="Please describe what happened, any error messages, and steps to reproduce if possible…"
              value={form.message}
              onChange={set("message")}
              required
            />
          </div>

          {status === "error" && (
            <p style={{ color: "var(--sm-primary)", marginBottom: 8 }}>
              Something went wrong. Email us at{" "}
              <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
            </p>
          )}

          <button
            type="submit"
            className="sm-btn sm-btn-primary"
            disabled={status === "sending"}
            style={{ border: "none", cursor: "pointer", minWidth: 160 }}
          >
            {status === "sending" ? "Sending…" : "Send request"}
          </button>

          <p style={{ fontSize: 13, color: "var(--sm-text-2)", marginTop: 16 }}>
            Or email us directly at{" "}
            <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
          </p>
        </form>
      )}
    </div>
  );
}
